import { Icon } from '@buggy/shared'
import { Button, Tooltip } from 'antd'
import React, { FunctionComponent } from 'react'
import styled from 'styled-components'
import UnlockButton from '@/components/kol/common/unlock-button'
import useIrmContentContext from '@/components/layout/hooks/use-irm-content-context'
import { createTourStepData } from '@/components/tour/utils'
import useIsJapan from '@/hooks/use-is-japan'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'

interface UnlockBannerProps {
  kolInfo?: KolSchemaModel
  onClickShowMockData: () => void
  showMockData: boolean
}

const UnlockBanner: FunctionComponent<UnlockBannerProps> = ({
  kolInfo,
  onClickShowMockData,
  showMockData,
}) => {
  const { formatMessage } = useIntl()

  const irmContentContext = useIrmContentContext()
  const isJapan = useIsJapan()

  const { withFreePermission } = usePermissions()

  return (
    <Box isSiderCollapsed={irmContentContext?.isSiderCollapsed}>
      <Container {...createTourStepData('social-performance', 2)}>
        <Icon color='white' fontSize={18} name='info' />
        <Text>
          {formatMessage({ id: 'kol:unlock_kol_detail_description' })}
        </Text>
        <Spacer />
        <ShowMockDataTooltipWrapper>
          <Tooltip
            getPopupContainer={(triggerNode: HTMLElement): HTMLElement =>
              triggerNode.parentElement ?? document.body
            }
            title={
              showMockData
                ? undefined
                : formatMessage({ id: 'kol:show_mock_data_tooltip' })
            }
          >
            <ShowMockDataButton
              ghost
              icon={<Icon name={showMockData ? 'eye-off' : 'eye'} />}
              onClick={onClickShowMockData}
            >
              {formatMessage({
                id: showMockData ? 'kol:hide_mock_data' : 'kol:show_mock_data',
              })}
            </ShowMockDataButton>
          </Tooltip>
        </ShowMockDataTooltipWrapper>
        <UnlockButton
          buttonI18nId={
            isJapan && withFreePermission
              ? 'vip:plan_year_contact_btn'
              : 'kol:unlock'
          }
          kolInfo={kolInfo}
        />
      </Container>
    </Box>
  )
}

const Box = styled.div<{ isSiderCollapsed?: boolean }>`
  width: 100%;
  position: fixed;
  bottom: 40px;
  left: 0;
  padding: 0 0 0
    ${({ isSiderCollapsed }): number => (isSiderCollapsed ? 80 : 200)}px;
  z-index: 10;
`

const Container = styled.div`
  display: flex;
  max-width: 800px;
  width: 94%;
  padding: 15px;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  border-radius: 5px;
  background: ${({ theme }): string => theme.colors.base.grey.grey1};
  @media (max-width: 1080px) {
    max-width: 700px;
  }
`

const Text = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey9};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  margin-left: 5px;
`

const Spacer = styled.div`
  flex: 1 0 10px;
`

const ShowMockDataButton = styled(Button)`
  border-radius: 4px;
  margin-right: 15px;
  font-weight: 600;
`

const ShowMockDataTooltipWrapper = styled.div`
  .ant-tooltip {
    --ant-color-bg-spotlight: ${({ theme }): string =>
      theme.colors.base.grey.grey9};

    .ant-tooltip-inner {
      color: ${({ theme }): string => theme.colors.base.grey.grey2};
      padding: 12px 16px;
    }
  }
`

export default UnlockBanner
